import { useQuery } from '@tanstack/react-query';

import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { queryKeys } from '@/lib/query-keys';
import type { MentionSuggestion } from '@/lib/schemas/mention';

import { mentionsApi } from './api';

/**
 * Who to offer for the `@…` being typed.
 *
 * Debounced, because this fires on every keystroke inside a mention. Results
 * are cached for a minute: typing "al", deleting to "a", typing "al" again is
 * one request, and people rarely rename mid-composition.
 */
export function useMentionSuggestions(query: string | null) {
  const debounced = useDebouncedValue(query ?? '', 150);
  const enabled = query !== null && debounced.length > 0;

  const result = useQuery({
    queryKey: queryKeys.mentionSuggestions(debounced),
    queryFn: ({ signal }) => mentionsApi.autocomplete(debounced, signal),
    enabled,
    staleTime: 60_000,
  });

  const suggestions: MentionSuggestion[] = enabled ? (result.data ?? []) : [];

  return { suggestions, isLoading: enabled && result.isLoading };
}
